/**
 * useComparePdfExport — État de l'export PDF de la page de comparaison.
 *
 * Réutilise les sessions déjà chargées par la page (aucun appel réseau) et la
 * plage d'analyse affichée (`analysis`, useAnalysisRange) ; le générateur est
 * chargé à la demande pour ne pas alourdir la page. `mention` (optionnelle)
 * remplace la mention de pied de page de la variante (lien de comparaison, D257).
 *
 * États exposés :
 *   - generating : génération en cours
 *   - error      : message lisible, ou null
 *   - disabled   : sessions pas prêtes (chargement, erreur) ou génération en cours
 *   - exportPdf  : () => Promise<void>, sans effet quand disabled
 */
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';

export default function useComparePdfExport({ loaded, alignMode, variant, analysis, mention, ready }) {
  const { t } = useTranslation();
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const disabled = !ready || generating;

  const exportPdf = useCallback(async () => {
    if (disabled) return;
    setGenerating(true);
    setError(null);
    try {
      const { generateComparePdf } = await import('../export/compare/comparePdf');
      await generateComparePdf({ loaded, alignMode, variant, analysis, mention });
    } catch (err) {
      Sentry.captureException(err, { extra: { stage: 'compare_pdf', variant } });
      setError(t('sessions.compare.pdf.error'));
    } finally {
      setGenerating(false);
    }
  }, [disabled, loaded, alignMode, variant, analysis, mention, t]);

  return { exportPdf, generating, error, disabled };
}
